/**
 * 住所/施設名 → 緯度経度 変換 (Mapbox Search Box API v1)
 *
 * 仕様参考: https://docs.mapbox.com/api/search/search-box/
 * - Geocoding v6 は住所系のみで POI を返さないため、 POI (施設名) も含む
 *   Search Box を採用。 「東京大学」「GRANDビル」のような名前で候補が出るようになる。
 * - country=jp, language=ja で日本国内・日本語結果を優先
 * - 失敗時は GeocodingError をthrowする
 * - 単一結果は `geocodeAddress`、 オートコンプリート用の複数候補は `geocodeSearch`
 */
import { MAPBOX_TOKEN } from './mapbox';
import { fetchWithTimeout } from '@/lib/net/fetch-timeout';
import type { LngLat } from '@/types/property';

export interface GeocodeResult {
  coords: LngLat;
  /** 短い名前 (例: "東京大学")。autocomplete の主見出しに使う。 */
  name: string;
  /** Mapboxが返した正規化済み住所 (例: "東京都文京区本郷7-3-1")。autocomplete のサブ見出し用。 */
  formattedAddress: string;
  /**
   * Search Box の feature_type (例: 'address' | 'poi' | 'block' | 'neighborhood')。
   * 座標の粒度を表す。建物単位の精度が要る用途 (物件の塗り取得) はこれを見ること。
   */
  featureType?: string;
}

/**
 * 建物を特定できる粒度の feature_type。
 *
 * これ以外 (neighborhood/locality/place/street/postcode 等) は「その辺り」を指す
 * 代表点で、建物から100m以上離れることがある。実測: "広島県広島市中区大手町1-2-1" は
 * feature_type=address なら実物から1m だが、neighborhood だと164m ずれる。
 */
const PRECISE_FEATURE_TYPES = new Set(['address', 'poi', 'block']);

/** 建物単位の粒度なら 1、「その辺り」の代表点なら 0。 */
function precision(featureType: string | undefined): number {
  return featureType && PRECISE_FEATURE_TYPES.has(featureType) ? 1 : 0;
}

export class GeocodingError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'GeocodingError';
  }
}

interface SearchBoxFeature {
  geometry?: { coordinates?: [number, number] };
  properties?: {
    name?: string;
    name_preferred?: string;
    address?: string;
    full_address?: string;
    place_formatted?: string;
    feature_type?: string;
  };
}
interface SearchBoxForwardResponse {
  features?: SearchBoxFeature[];
}

/**
 * 検索語に対する名前の近さ (物件らしさ) スコア。大きいほど検索語に近い。
 * Search Box はビル名で検索してもテナント店舗 (「〇〇京橋エドグラン店」) を
 * 上位に返し、肝心のビル (名前 = 検索語) が最後に埋もれる。名前が検索語に
 * 近い候補ほど上位にするための指標。
 */
function nameCloseness(name: string, query: string): number {
  const n = name.trim().toLowerCase();
  const q = query.trim().toLowerCase();
  if (!n || !q) return 0;
  if (n === q) return 4; // 完全一致 = そのビル/物件そのもの
  if (n.startsWith(q) || q.startsWith(n)) return 3; // 前方一致
  if (n.includes(q)) return 2; // 店舗名が物件名を内包 (テナント店)
  if (q.includes(n)) return 1;
  return 0; // 検索語と無関係な名前は Mapbox 順を維持
}

/**
 * Mapbox の関連度順を尊重しつつ、名前が検索語に近い候補を安定ソートで上位へ。
 * (物件名検索で店舗ばかりが上位に来てビルを選べない問題の対策)
 *
 * 名前の近さが同点のときは、座標の粒度が細かい候補を優先する。
 * 住所で検索すると「番地」と「町名」の候補が同点になりやすく (どちらも
 * 検索語に部分一致する)、Mapbox の返却順次第で町名の代表点が採用されて
 * しまう。実測: "広島県広島市中区大手町1-2-1" で neighborhood 候補が
 * 採用され、建物から164m離れた点が座標として保存されていた。
 * 名前の近さを主基準に保つことで、POI検索 (「東京大学」= 完全一致)
 * の挙動は変わらない。
 */
function rankByNameCloseness(
  results: GeocodeResult[],
  query: string,
): GeocodeResult[] {
  return results
    .map((r, i) => ({
      r,
      i,
      c: nameCloseness(r.name, query),
      p: precision(r.featureType),
    }))
    .sort((a, b) => b.c - a.c || b.p - a.p || a.i - b.i)
    .map((x) => x.r);
}

/**
 * 住所/施設名から最も確からしい単一結果を返す。 失敗時は GeocodingError。
 * 複数候補を取得し名前の近さで並べ替えてから先頭を採用 (物件名でもビルを優先)。
 */
export async function geocodeAddress(
  address: string,
): Promise<GeocodeResult> {
  const trimmed = address.trim();
  if (!trimmed) {
    throw new GeocodingError('住所が空です');
  }
  const results = await geocodeSearch(trimmed, 5);
  if (results.length === 0) {
    throw new GeocodingError(`住所が見つかりませんでした: ${trimmed}`);
  }
  return results[0]!;
}

/**
 * 入力テキストに対する複数候補を Search Box API で取得。
 * POI (施設名) + 住所 + 行政区分 を含むため、 「東京大学」 でも候補が出る。
 * オートコンプリート用 (debounce推奨)。 結果0件は空配列、 接続/APIエラーは throw。
 */
export async function geocodeSearch(
  query: string,
  limit = 5,
): Promise<GeocodeResult[]> {
  if (!MAPBOX_TOKEN) {
    throw new GeocodingError(
      'Mapboxトークンが未設定です。.envの VITE_MAPBOX_PUBLIC_TOKEN を設定してください。',
    );
  }
  const trimmed = query.trim();
  if (!trimmed) return [];

  const url = new URL(
    'https://api.mapbox.com/search/searchbox/v1/forward',
  );
  url.searchParams.set('q', trimmed);
  url.searchParams.set('country', 'jp');
  url.searchParams.set('language', 'ja');
  url.searchParams.set('limit', String(Math.max(1, Math.min(10, limit))));
  url.searchParams.set('access_token', MAPBOX_TOKEN);

  let response: Response;
  try {
    response = await fetchWithTimeout(url.toString());
  } catch (e) {
    throw new GeocodingError(
      'Mapbox Search Box APIへの接続に失敗しました',
      e,
    );
  }
  if (!response.ok) {
    throw new GeocodingError(
      `Mapbox Search Box APIがエラーを返しました (${response.status})`,
    );
  }
  const json = (await response.json()) as SearchBoxForwardResponse;
  const out: GeocodeResult[] = [];
  for (const f of json.features ?? []) {
    const c = f.geometry?.coordinates;
    if (!c || c.length !== 2) continue;
    const p = f.properties ?? {};
    const name = p.name_preferred ?? p.name ?? p.place_formatted ?? trimmed;
    const formattedAddress =
      p.full_address ?? p.place_formatted ?? p.address ?? name;
    out.push({
      coords: { lng: c[0]!, lat: c[1]! },
      name,
      formattedAddress,
      featureType: p.feature_type,
    });
  }
  // 名前が検索語に近い候補 (= 物件そのもの) を上位へ並べ替える。
  return rankByNameCloseness(out, trimmed);
}
